<template>
    <v-container fluid class="shop-room px-0">
        <div>
            <div style="position: relative">
                <div :class="{ 'blur-content': categoryModal }">
                    <ShopHeader />
                    <ShopFilterPanel
                        :productList="productList"
                        v-if="facets"
                        :facets="facets"
                        :validFacets="validFacets"
                        class="mb-2"
                    />
                    <ShopControlPanel v-if="modifiedFacets && productListWithAppliedPricelist.length > 0" />
                    <ShopTable
                        :key="componentKey"
                        v-if="modifiedFacets"
                        :facets="modifiedFacets"
                        @setProductList="setProductList($event)"
                        :validFacets="validFacets"
                        class="mb-2"
                    />
                    <ShopControlPanel v-if="modifiedFacets && productListWithAppliedPricelist.length > 0" />
                </div>
                <div v-if="!modifiedFacets" style="width:1495px;height:2000px;top:75px;position: absolute;" class="d-flex justify-center align-start">
                        <v-progress-circular
                        :size="50"
                        color="primary"
                        indeterminate
                        ></v-progress-circular>
                </div>
                <v-overlay :absolute="true" z-index="4" :value="categoryModal">
                    <ShopPageNavigationMenu
                        width="70%"
                        :showHeading="true"
                        @handleCategoryModal="categoryModal = !categoryModal"
                    />
                </v-overlay>
            </div>
        </div>
    </v-container>
</template>

<script>
import ShopHeader from '~/components/shop/shopUtilities/ShopHeader.vue'
import ShopFilterPanel from '~/components/shop/shopUtilities/ShopFilterPanel.vue'
import ShopTable from '~/components/shop/ShopTable.vue'
import CategoryListing from '~/components/shop/shopUtilities/CategoryListing.vue'
import ShopPageNavigationMenu from '~/components/shop/shopUtilities/ShopPageNavigationMenu.vue'
import ShopControlPanel from '~/components/shop/shopUtilities/ShopControlPanel.vue'
import { client } from '~/plugins/global.js'
import { mapGetters } from 'vuex'
import { interpolateDOLStartersSlug } from '~/util/dol-staters-helper'

export default {
    name: 'ShopRoom',
    components: {
        ShopFilterPanel,
        ShopHeader,
        ShopTable,
        CategoryListing,
        ShopPageNavigationMenu,
        ShopControlPanel,
    },
    data() {
        return {
            attributes: [],
            productList: [],
            categoryModal: false,
            componentKey: true,
        }
    },
    fetch() {
        this.fetchAttributes()
    },
    fetchOnServer: false,
    async mounted() {
        this.categoryModal = !this.isCategorySelected
    },
    watch: {
        '$route.query': {
            handler: function () {
                if (Object.keys(this.$route.query).length === 0) {
                    this.categoryModal = true
                } else {
                    this.categoryModal = false
                }
            },
        },
        '$route.query.Categories': {
            handler: function () {
                this.componentKey = !this.componentKey
            },
        },
    },
    methods: {
        setModalValue() {
            this.$nextTick(() => {
                this.categoryModal = !this.isCategorySelected
            })
        },
        async fetchAttributes() {
            const groq = `*[_type == "attributeTitle" && isPublic == true] | order(title.en asc) { title, "categories": categories[]-> title.en}`
            this.attributes = await client.fetch(groq)
        },
        isBase64(str) {
            if (typeof str !== 'string' && !process.client) {
                return false
            }
            try {
                return window.btoa(window.atob(str)) == str
            } catch (err) {
                return false
            }
        },
        createProductLink(item) {
            if (item.parent) {
                const path = this.localePath({
                    name: 'products-slug',
                    params: {
                        slug: interpolateDOLStartersSlug(item.parent[this.$i18n.locale].current),
                    },
                })
                if (this.isCustomProduct) {
                    return `${path}/${item.pn}/?isCustom=${this.isCustomProduct.hash}`
                } else {
                    return `${path}/${item.pn}/`
                }
            } else {
                return ''
            }
        },
        setProductList(list) {
            this.productList = list
        },
    },
    computed: {
        ...mapGetters({
            categoryTree: 'common/categoryTree',
            productListWithAppliedPricelist:
                'shop/productListWithAppliedPricelist',
        }),
        facets() {
            if (this.attributes?.length > 0 && this.categoryTree?.length > 0) {
                const dynamicFacets = this.attributes?.map((f) => {
                    return {
                        name: f.title.en,
                        type: 'multiple',
                        property: `attributes.${f.title.en}.keyword`,
                        react: [],
                        aggregate: true,
                        shouldAggregate: (filters) => {
                            const allCategories = f.categories.flatMap(
                                (category) => {
                                    const cat = this.categoryTree.find(
                                        (c) => c.title.en === category
                                    )
                                    // console.log('CAT IS', cat)
                                    const subCats = cat
                                        ? cat.subCategories.map(
                                              (s) => s.title.en
                                          )
                                        : []
                                    return [category, ...subCats]
                                }
                            )
                            const categoryFacet = filters.find(
                                (filter) => filter?.name === 'Categories'
                            )

                            return (
                                _.intersection(
                                    categoryFacet.value || [],
                                    allCategories
                                ).length > 0
                            )
                        },
                    }
                })
                return [
                    {
                        name: 'Search',
                        type: 'custom',
                        query: (v) => ({
                            bool: {
                                should: [
                                    {
                                        match: {
                                            name_es: {
                                                query: v,
                                                fuzziness: 0,
                                            },
                                        },
                                    },
                                    {
                                        term: {
                                            pn: v,
                                        },
                                    },
                                ],
                            },
                        }),
                        react: [],
                        aggregate: false,
                    },
                    {
                        name: 'Categories',
                        property: 'categories',
                        react: [],
                        type: 'multiple',
                        aggregate: false,
                    },
                    ...dynamicFacets,
                ]
            } else {
                return undefined
            }
        },
        modifiedFacets() {
            if (!process.client) {
                return undefined
            }
            const decodedFilterObj =
                this.$route.query.filter &&
                this.isBase64(this.$route.query.filter)
                    ? JSON.parse(window.atob(this.$route.query.filter))
                    : null
            return this.facets?.map((f) => {
                if (f.type === 'range') {
                    return {
                        ...f,
                        value: decodedFilterObj
                            ? decodedFilterObj[f.name]
                            : null,
                        data: { max: 0, min: 0 },
                    }
                } else if (f.type === 'multiple' && f.name !== 'Categories') {
                    return {
                        ...f,
                        value: decodedFilterObj
                            ? decodedFilterObj[f.name]
                            : null,
                        data: [],
                    }
                } else {
                    return {
                        ...f,
                        value: this.$route.query[f.name]
                            ? [this.$route.query[f.name]]
                            : null,
                        data: [],
                    }
                }
            })
        },
        isCategorySelected() {
            return this.$route.query.Categories
        },
        validFacets() {
            return this.attributes.filter((f) => {
                const cat =
                    (this.$route.query.type || 'product') === 'product'
                        ? this.selectedCategory
                        : `${this.selectedCategory} Accessories`
                return f.categories.includes(cat)
            })
        },
        selectedCategory() {
            return this.$route.query.Categories
        },
    },
}
</script>

<style lang="scss" scoped>
.blur-content {
    filter: blur(4px);
    min-height: 750px;
}
</style>
